import { useQuery, useMutation } from '@apollo/client';
import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Grid,
  MenuItem,
  Divider,
} from '@mui/material';
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { useAuth } from '../contexts/AuthContext';
import type {
  EncryptedVaultItem,
  DecryptedVaultItem,
  PasswordPayload,
  TOTPPayload,
  SecureNotePayload,
  CreditCardPayload,
  ContactPayload,
  ContactAddress,
} from '../types';
import { encryptPayload, decryptItemKey, decryptPayload } from '../crypto/encryption';
import { COUNTRIES } from '../utils/countries';
import { VAULT_ITEM_QUERY, UPDATE_VAULT_ITEM_MUTATION } from '@/services/graphql';

export default function EditItem() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { vaultKey } = useAuth();

  const [decryptedItem, setDecryptedItem] = useState<DecryptedVaultItem | null>(null);
  const [isFavorite, setIsFavorite] = useState(false);
  const [error, setError] = useState('');

  // Common fields
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');

  // Password fields
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [websites, setWebsites] = useState<string[]>(['']);
  const [showPassword, setShowPassword] = useState(false);

  // TOTP fields
  const [secret, setSecret] = useState('');

  // Secure Note fields
  const [secureNote, setSecureNote] = useState('');

  // Credit Card fields
  const [cardholderName, setCardholderName] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [expirationDate, setExpirationDate] = useState('');
  const [cvv, setCvv] = useState('');
  const [cardPin, setCardPin] = useState('');
  const [cardPostalCode, setCardPostalCode] = useState('');
  const [showCvv, setShowCvv] = useState(false);
  const [showPin, setShowPin] = useState(false);

  // Contact fields
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState<ContactAddress>({});

  const { data, loading: queryLoading } = useQuery(VAULT_ITEM_QUERY, {
    variables: { id },
    skip: !id,
  });

  const [updateItem, { loading: updateLoading }] = useMutation(UPDATE_VAULT_ITEM_MUTATION, {
    onCompleted: () => {
      navigate(`/vault/${id}`);
    },
    onError: (err) => {
      setError(err.message);
    },
  });

  // Decrypt and populate form
  useEffect(() => {
    if (!data?.vaultItem || !vaultKey) return;

    const item = data.vaultItem as EncryptedVaultItem;
    const itemKey = decryptItemKey(item.itemKeyEncrypted, item.itemKeyNonce, vaultKey);

    if (!itemKey) return;

    const payload = decryptPayload(item.encryptedPayload, item.payloadNonce, itemKey);
    if (!payload) return;

    setDecryptedItem({
      id: item.id,
      type: item.type,
      payload,
      isFavorite: item.isFavorite,
      createdAt: item.createdAt,
      updatedAt: item.updatedAt,
      isOwner: item.isOwner,
      ownerId: item.ownerId,
      ownerEmail: item.ownerEmail,
      owner: item.owner,
      shares: item.shares || [],
      itemKey,
    });

    setTitle(payload.title);
    setIsFavorite(item.isFavorite);

    if (payload.type === 'password') {
      setUsername(payload.username || '');
      setPassword(payload.password || '');
      setWebsites(payload.websites?.length ? payload.websites : ['']);
      setNotes(payload.notes || '');
    } else if (payload.type === 'totp') {
      setSecret(payload.secret);
      setNotes(payload.notes || '');
    } else if (payload.type === 'secure_note') {
      setSecureNote(payload.secureNote);
    } else if (payload.type === 'credit_card') {
      setCardholderName(payload.cardholderName || '');
      setCardNumber(payload.cardNumber);
      setExpirationDate(payload.expirationDate || '');
      setCvv(payload.cvv || '');
      setCardPin(payload.cardPin || '');
      setCardPostalCode(payload.postalCode || '');
      setNotes(payload.notes || '');
    } else if (payload.type === 'contact') {
      setFullName(payload.fullName);
      setEmail(payload.email || '');
      setPhone(payload.phone || '');
      setAddress(payload.address || {});
      setNotes(payload.notes || '');
    }
  }, [data, vaultKey]);

  const addWebsite = () => {
    setWebsites([...websites, '']);
  };

  const removeWebsite = (index: number) => {
    if (websites.length > 1) {
      setWebsites(websites.filter((_, i) => i !== index));
    }
  };

  const updateWebsite = (index: number, value: string) => {
    const updated = [...websites];
    updated[index] = value;
    setWebsites(updated);
  };

  const validateUrl = (url: string): boolean => {
    if (!url) return true;
    try {
      // If URL has protocol, validate directly; otherwise add https://
      new URL(/^https?:\/\//i.test(url) ? url : `https://${url}`);
      return true;
    } catch {
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!decryptedItem || !vaultKey) {
      setError('Item data not available');
      return;
    }

    if (!title.trim()) {
      setError('Please enter a title');
      return;
    }

    try {
      let payload:
        | PasswordPayload
        | TOTPPayload
        | SecureNotePayload
        | CreditCardPayload
        | ContactPayload;

      if (decryptedItem.type === 'password') {
        for (const url of websites) {
          if (url && !validateUrl(url)) {
            setError(`Invalid URL: ${url}`);
            return;
          }
        }

        if (!password.trim()) {
          setError('Password is required');
          return;
        }

        payload = {
          type: 'password',
          title,
          username: username || undefined,
          password,
          websites: websites.filter((url) => url.trim()),
          notes: notes || undefined,
        };
      } else if (decryptedItem.type === 'totp') {
        const existingPayload = decryptedItem.payload as TOTPPayload;
        payload = {
          type: 'totp',
          title,
          secret, // Keep the existing secret (shouldn't be changed)
          algorithm: existingPayload.algorithm || 'SHA1',
          digits: existingPayload.digits || 6,
          period: existingPayload.period || 30,
          notes: notes || undefined,
        };
      } else if (decryptedItem.type === 'secure_note') {
        if (!secureNote.trim()) {
          setError('Note content is required');
          return;
        }

        payload = {
          type: 'secure_note',
          title,
          secureNote,
        };
      } else if (decryptedItem.type === 'credit_card') {
        if (!cardNumber.trim()) {
          setError('Card number is required');
          return;
        }

        if (expirationDate && !/^(0[1-9]|1[0-2])\/\d{2}$/.test(expirationDate)) {
          setError('Expiration date must be in MM/YY format');
          return;
        }

        payload = {
          type: 'credit_card',
          title,
          cardholderName: cardholderName || undefined,
          cardNumber,
          expirationDate: expirationDate || undefined,
          cvv: cvv || undefined,
          cardPin: cardPin || undefined,
          postalCode: cardPostalCode || undefined,
          notes: notes || undefined,
        };
      } else if (decryptedItem.type === 'contact') {
        if (!fullName.trim()) {
          setError('Full name is required');
          return;
        }

        payload = {
          type: 'contact',
          title,
          fullName,
          email: email || undefined,
          phone: phone || undefined,
          address: Object.keys(address).some((k) => address[k as keyof ContactAddress])
            ? address
            : undefined,
          notes: notes || undefined,
        };
      } else {
        setError('Invalid item type');
        return;
      }

      // Re-encrypt with the same item key
      const { encryptedPayload, payloadNonce } = encryptPayload(payload, decryptedItem.itemKey);

      await updateItem({
        variables: {
          input: {
            id,
            encryptedPayload,
            payloadNonce,
            isFavorite,
          },
        },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update item');
    }
  };

  if (queryLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!decryptedItem) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Alert severity="error">Item not found or failed to decrypt</Alert>
      </Container>
    );
  }

  const renderPasswordForm = () => (
    <>
      <TextField
        fullWidth
        label="Username / Email"
        value={username}
        onChange={(e) => setUsername(e.target.value)}
        margin="normal"
        autoComplete="off"
      />

      <TextField
        fullWidth
        label="Password"
        type={showPassword ? 'text' : 'password'}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        margin="normal"
        required
        autoComplete="off"
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={() => setShowPassword(!showPassword)}>
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
        Websites
      </Typography>
      {websites.map((url, index) => (
        <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'flex-start' }}>
          <TextField
            size="small"
            label="URL"
            placeholder="https://example.com"
            value={url}
            onChange={(e) => updateWebsite(index, e.target.value)}
            sx={{ flex: 1 }}
          />
          <IconButton
            size="small"
            onClick={() => removeWebsite(index)}
            disabled={websites.length === 1}
            color="error"
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      ))}
      <Button size="small" startIcon={<AddIcon />} onClick={addWebsite} sx={{ mb: 2 }}>
        Add Website
      </Button>
    </>
  );

  const renderTOTPForm = () => (
    <>
      <TextField
        fullWidth
        label="Secret Key"
        value={secret}
        onChange={(e) => setSecret(e.target.value)}
        margin="normal"
        required
        disabled // Don't allow editing secret
        helperText="Secret cannot be changed after creation"
      />
    </>
  );

  const renderSecureNoteForm = () => (
    <TextField
      fullWidth
      label="Secure Note"
      value={secureNote}
      onChange={(e) => setSecureNote(e.target.value)}
      margin="normal"
      required
      multiline
      rows={8}
    />
  );

  const renderCreditCardForm = () => (
    <>
      <TextField
        fullWidth
        label="Cardholder Name"
        value={cardholderName}
        onChange={(e) => setCardholderName(e.target.value)}
        margin="normal"
      />

      <TextField
        fullWidth
        label="Card Number"
        value={cardNumber}
        onChange={(e) => setCardNumber(e.target.value)}
        margin="normal"
        required
      />

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Expiration Date"
            value={expirationDate}
            onChange={(e) => setExpirationDate(e.target.value)}
            margin="normal"
            placeholder="MM/YY"
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="CVV"
            type={showCvv ? 'text' : 'password'}
            value={cvv}
            onChange={(e) => setCvv(e.target.value)}
            margin="normal"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowCvv(!showCvv)} size="small">
                    {showCvv ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="PIN"
            type={showPin ? 'text' : 'password'}
            value={cardPin}
            onChange={(e) => setCardPin(e.target.value)}
            margin="normal"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPin(!showPin)} size="small">
                    {showPin ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Billing Postal Code"
            value={cardPostalCode}
            onChange={(e) => setCardPostalCode(e.target.value)}
            margin="normal"
          />
        </Grid>
      </Grid>
    </>
  );

  const renderContactForm = () => (
    <>
      <TextField
        fullWidth
        label="Full Name"
        value={fullName}
        onChange={(e) => setFullName(e.target.value)}
        margin="normal"
        required
      />

      <TextField
        fullWidth
        label="Email"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        margin="normal"
      />

      <TextField
        fullWidth
        label="Phone"
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        margin="normal"
      />

      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Address
      </Typography>

      <TextField
        fullWidth
        label="Address Line 1"
        value={address.line1 || ''}
        onChange={(e) => setAddress({ ...address, line1: e.target.value })}
        margin="normal"
      />

      <TextField
        fullWidth
        label="Address Line 2"
        value={address.line2 || ''}
        onChange={(e) => setAddress({ ...address, line2: e.target.value })}
        margin="normal"
      />

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="City"
            value={address.city || ''}
            onChange={(e) => setAddress({ ...address, city: e.target.value })}
            margin="normal"
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="State / Province"
            value={address.stateOrProvince || ''}
            onChange={(e) => setAddress({ ...address, stateOrProvince: e.target.value })}
            margin="normal"
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Postal Code"
            value={address.postalCode || ''}
            onChange={(e) => setAddress({ ...address, postalCode: e.target.value })}
            margin="normal"
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            select
            label="Country"
            value={address.countryOrRegion || ''}
            onChange={(e) => setAddress({ ...address, countryOrRegion: e.target.value })}
            margin="normal"
          >
            <MenuItem value="">None</MenuItem>
            {COUNTRIES.map((country) => (
              <MenuItem key={country.code} value={country.code}>
                {country.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>
    </>
  );

  return (
    <Box sx={{ flex: 1 }}>
      <Container maxWidth="sm" sx={{ py: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton onClick={() => navigate(`/vault/${id}`)} sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" fontWeight={600}>
            Edit Item
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Paper sx={{ p: 3 }}>
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              margin="normal"
              required
              autoFocus
            />

            {decryptedItem.type === 'password' && renderPasswordForm()}
            {decryptedItem.type === 'totp' && renderTOTPForm()}
            {decryptedItem.type === 'secure_note' && renderSecureNoteForm()}
            {decryptedItem.type === 'credit_card' && renderCreditCardForm()}
            {decryptedItem.type === 'contact' && renderContactForm()}

            {decryptedItem.type !== 'secure_note' && (
              <TextField
                fullWidth
                label="Notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                margin="normal"
                multiline
                rows={3}
              />
            )}

            <FormControlLabel
              control={
                <Switch checked={isFavorite} onChange={(e) => setIsFavorite(e.target.checked)} />
              }
              label="Favorite"
              sx={{ mt: 2 }}
            />

            <Button
              fullWidth
              type="submit"
              variant="contained"
              size="large"
              disabled={updateLoading}
              sx={{ mt: 3 }}
            >
              {updateLoading ? 'Saving...' : 'Save Changes'}
            </Button>
          </form>
        </Paper>
      </Container>
    </Box>
  );
}
